import { useState, useEffect, useRef, type ReactElement } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  HelpCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { BrowserHistoryDetailDialog } from '@/components/shared/shadow-ai/BrowserHistoryDetailDialog';
import type { ShadowAiToolListItem } from '@kirin/api-client/contracts/shadow-ai';
import type {
  ShadowAiSortField,
  SortOrder,
} from '@kirin/api-client/api/shadow-ai';
import { cn } from '@/lib/utils';
import {
  CategoryBadge,
  SourceBadges,
  ExtrasCard,
  isRecentlyDetected,
} from './components';

function SortableHeader({
  label,
  field,
  currentSortBy,
  currentSortOrder,
  onSort,
}: {
  label: string;
  field: ShadowAiSortField;
  currentSortBy?: ShadowAiSortField;
  currentSortOrder?: SortOrder;
  onSort: (field: ShadowAiSortField) => void;
}): ReactElement {
  const isActive = currentSortBy === field;

  return (
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      onClick={() => onSort(field)}
    >
      {label}
      {isActive ? (
        currentSortOrder === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

function ToolRow({
  tool,
  isHighlighted,
  rowRef,
}: {
  tool: ShadowAiToolListItem;
  isHighlighted?: boolean;
  rowRef?: React.RefObject<HTMLTableRowElement | null>;
}): ReactElement {
  const [expanded, setExpanded] = useState(isHighlighted ?? false);
  const isNew = isRecentlyDetected(tool.addedAt);

  useEffect(() => {
    if (isHighlighted && rowRef?.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted, rowRef]);

  return (
    <>
      <TableRow
        ref={isHighlighted ? rowRef : undefined}
        className={cn(
          'cursor-pointer border-b hover:bg-muted/50',
          isHighlighted && 'ring-2 ring-blue-500 animate-pulse'
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell className="w-8 h-12 px-3 py-2">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </TableCell>
        <TableCell className="font-medium h-12 px-3 py-2">
          <div className="flex items-center gap-2">
            {tool.name}
            {isNew && (
              <Badge className="bg-blue-500 hover:bg-blue-600 text-xs px-1.5 py-0">
                NEW
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="h-12 px-3 py-2">
          <CategoryBadge category={tool.category} />
        </TableCell>
        <TableCell className="text-muted-foreground h-12 px-3 py-2">
          {tool.addedAt ? (
            <RelativeTimeWithTooltip date={tool.addedAt} />
          ) : (
            <span>Unknown</span>
          )}
        </TableCell>
        <TableCell className="text-muted-foreground h-12 px-3 py-2">
          {tool.lastUsed ? (
            <RelativeTimeWithTooltip date={tool.lastUsed} />
          ) : (
            <span>Unknown</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={5} className="py-3">
            <div className="pl-8 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground whitespace-nowrap">
                  Detected via:
                </span>
                <SourceBadges sources={tool.sources} />
              </div>
              {tool.extras && <ExtrasCard extras={tool.extras} />}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function ToolTable({
  tools,
  emptyMessage,
  sortBy,
  sortOrder,
  onSort,
  highlightToolId,
}: {
  tools: ShadowAiToolListItem[];
  emptyMessage: string;
  sortBy?: ShadowAiSortField;
  sortOrder?: SortOrder;
  onSort: (field: ShadowAiSortField) => void;
  highlightToolId?: string | null;
}): ReactElement {
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);

  return (
    <div className="rounded-md border border-table-border overflow-hidden">
      <Table className="text-xs">
        <TableHeader className="sticky top-0 z-20 border-b bg-muted">
          <TableRow className="!bg-transparent hover:!bg-transparent">
            <TableHead className="w-8" />
            <TableHead>
              <SortableHeader
                label="Name"
                field="name"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Category"
                field="category"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="First Detected"
                field="addedAt"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Last Seen"
                field="lastUsed"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tools.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center py-6 text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            tools.map((tool, index) => (
              <ToolRow
                key={`${tool.toolId}-${index}`}
                tool={tool}
                isHighlighted={highlightToolId === tool.toolId}
                rowRef={highlightedRowRef}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function TrendCell({
  extras,
  addedAt,
}: {
  extras?: Record<string, string>;
  addedAt?: string | null;
}): ReactElement {
  const [isNew] = useState(() => {
    if (!addedAt) return false;
    const addedTime = new Date(addedAt).getTime();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - addedTime < sevenDaysMs;
  });

  const thisWeek = extras?.visitsThisWeek
    ? parseInt(extras.visitsThisWeek, 10)
    : 0;
  const lastWeek = extras?.visitsLastWeek
    ? parseInt(extras.visitsLastWeek, 10)
    : 0;

  if (!extras?.visitsThisWeek) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (isNew) {
    return (
      <span className="text-blue-600 font-medium flex items-center gap-1">
        New
      </span>
    );
  }

  if (lastWeek === 0) {
    if (thisWeek > 0) {
      return (
        <span className="text-yellow-600 font-medium flex items-center gap-1">
          <TrendingUp className="h-3.5 w-3.5" />
          Resumed
        </span>
      );
    }
    return <span className="text-muted-foreground">Inactive</span>;
  }

  const changePercent = Math.round(((thisWeek - lastWeek) / lastWeek) * 100);

  if (Math.abs(changePercent) <= 10) {
    return (
      <span className="text-muted-foreground flex items-center gap-1">
        <Minus className="h-3.5 w-3.5" />
        Stable
      </span>
    );
  }

  if (changePercent > 0) {
    return (
      <span className="text-green-600 font-medium flex items-center gap-1">
        <TrendingUp className="h-3.5 w-3.5" />+{changePercent}%
      </span>
    );
  }

  return (
    <span className="text-red-600 flex items-center gap-1">
      <TrendingDown className="h-3.5 w-3.5" />
      {changePercent}%
    </span>
  );
}

function BrowserHistoryRow({
  tool,
  isHighlighted,
  rowRef,
  onSelect,
}: {
  tool: ShadowAiToolListItem;
  isHighlighted?: boolean;
  rowRef?: React.RefObject<HTMLTableRowElement | null>;
  onSelect: (tool: ShadowAiToolListItem) => void;
}): ReactElement {
  const domain = tool.toolId;

  useEffect(() => {
    if (isHighlighted && rowRef?.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted, rowRef]);

  return (
    <TableRow
      ref={isHighlighted ? rowRef : undefined}
      className={cn(
        'border-b hover:bg-muted/50 cursor-pointer',
        isHighlighted && 'ring-2 ring-blue-500 animate-pulse'
      )}
      onClick={() => onSelect(tool)}
    >
      <TableCell>
        <span className="text-sm font-mono text-muted-foreground">
          {domain}
        </span>
      </TableCell>
      <TableCell className="font-medium">{tool.name}</TableCell>
      <TableCell>
        <CategoryBadge category={tool.category} />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {tool.addedAt ? (
          <RelativeTimeWithTooltip date={tool.addedAt} />
        ) : (
          <span>Unknown</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {tool.lastUsed ? (
          <RelativeTimeWithTooltip date={tool.lastUsed} />
        ) : (
          <span>Unknown</span>
        )}
      </TableCell>
      <TableCell>
        <TrendCell extras={tool.extras ?? undefined} addedAt={tool.addedAt} />
      </TableCell>
    </TableRow>
  );
}

export function BrowserHistoryTable({
  tools,
  emptyMessage,
  sortBy,
  sortOrder,
  onSort,
  highlightToolId,
}: {
  tools: ShadowAiToolListItem[];
  emptyMessage: string;
  sortBy?: ShadowAiSortField;
  sortOrder?: SortOrder;
  onSort: (field: ShadowAiSortField) => void;
  highlightToolId?: string | null;
}): ReactElement {
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const [selected, setSelected] = useState<ShadowAiToolListItem | null>(null);
  return (
    <div className="rounded-md border border-table-border overflow-hidden">
      <Table className="text-xs">
        <TableHeader className="sticky top-0 z-20 border-b bg-muted">
          <TableRow className="!bg-transparent hover:!bg-transparent">
            <TableHead>URL</TableHead>
            <TableHead>
              <SortableHeader
                label="Name"
                field="name"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Category"
                field="category"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="First Detected"
                field="addedAt"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Last Seen"
                field="lastUsed"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <div className="flex items-center gap-1">
                Trend
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-[200px]">
                      Compares visits this week vs last week to show usage
                      changes
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tools.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center py-6 text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            tools.map((tool, index) => (
              <BrowserHistoryRow
                key={`${tool.toolId}-${index}`}
                tool={tool}
                isHighlighted={highlightToolId === tool.toolId}
                rowRef={highlightedRowRef}
                onSelect={setSelected}
              />
            ))
          )}
        </TableBody>
      </Table>
      <BrowserHistoryDetailDialog
        tool={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
